import { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useGlobalStore } from '../../application/store/store.tsx';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes.ts';
import AvailableDrugs from './components/AvailableDrugs.tsx';
import AvailableStorages from './components/AvailableStorages.tsx';
import TextField from '@mui/material/TextField';
import {
    Stepper,
    Step,
    StepLabel,
    Box,
    Button,
    InputAdornment,
} from '@mui/material';
import DisposedCause from './components/DisposedCause.tsx';
import Summary from './components/Summary.tsx';
import Sign from './components/Sign.tsx';
import {
    TransactionOverview,
    TransactionType,
} from '../../application/types/transactionType.ts';
import IconTextField from '../../common/components/IconTextfield.tsx';
import SignedSuccess from './components/SignedSuccess.tsx';
import Visibility from '../../common/components/Visibility.tsx';
import ScanningDetector from './components/ScanningDetector.tsx';
import KeyboardListener from '../../common/components/KeyboardListener.tsx';
import BarCodeOnOffSwitch from '../../common/components/BarCodeOnOffSwitch.tsx';
import { StorageType } from '../../application/types/storageType.ts';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const steps = ['Lagerställe', 'Läkemedel', 'Orsak', 'Sammanfattning'];

const Disposed = () => {
    const selectedDrug = useGlobalStore((state) => state.selectedDrug);
    const setSelectedDrug = useGlobalStore((state) => state.setSelectedDrug);

    const selectedStorage = useGlobalStore((state) => state.selectedStorage);
    const setSelectedStorage = useGlobalStore(
        (state) => state.setSelectedStorage
    );

    const setSelectedEmergency = useGlobalStore(
        (state) => state.setSelectedEmergency
    );

    const quantityChanged = useGlobalStore((state) => state.quantityChanged);

    const disposedQuantity = useGlobalStore((state) => state.disposedQuantity);
    const setDisposedQuantity = useGlobalStore(
        (state) => state.setDisposedQuantity
    );

    const storageRemaining = useGlobalStore((state) => state.storageRemaining);
    const setstorageRemaining = useGlobalStore(
        (state) => state.setstorageRemaining
    );

    const resetSelectedDrug = useGlobalStore(
        (state) => state.resetSelectedDrug
    );
    const resetStorage = useGlobalStore((state) => state.resetAvailableStorage);

    const resetDisposedCause = useGlobalStore(
        (state) => state.resetDisposedCause
    );

    const resetDisposedFormData = useGlobalStore(
        (state) => state.resetDisposedFormData
    );

    const disposedCause = useGlobalStore((state) => state.disposedCause);

    const depositOverview = useGlobalStore((state) => state.depositOverview);
    const formRegistrationMethod = useGlobalStore(
        (state) => state.formRegistrationMethod
    );

    const [isStepperOpen, setIsStepperOpen] = useState(false);

    const toggleStepper = () => {
        setIsStepperOpen(!isStepperOpen);
    };

    useEffect(() => {
        return () => {
            resetSelectedDrug();
            resetStorage();
            resetDisposedCause();
            resetDisposedFormData();
        };
    }, [
        resetSelectedDrug,
        resetStorage,
        resetDisposedCause,
        resetDisposedFormData,
    ]);

    const stepContents = [
        `${selectedStorage?.name ?? ''}`,
        `${selectedDrug?.drugName ?? ''} ${selectedDrug?.drugName ? `(${selectedDrug?.concentration ?? ''} ${selectedDrug?.concentrationUnit ?? ''}, ${selectedDrug?.quantity ?? ''}ml)` : ''}`,
        '',
    ];

    const [activeStep, setActiveStep] = useState(0);
    const [searchTermDrugs, setSearchTermDrugs] = useState<string>('');
    const [searchTermStorage, setSearchTermStorage] = useState<string>('');

    const navigate = useNavigate();

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleNavigateBack = () => {
        // Clear all state before navigating
        resetSelectedDrug();
        resetStorage();

        // Reset overview state
        useGlobalStore.getState().resetOverviews();

        navigate(NavigationRoutes.transaction);
    };

    const handleSelectDrug = (drug) => {
        const formattedDrugValue = `${selectedDrug?.drugName ?? ''} ${selectedDrug?.drugName ? `(${selectedDrug?.concentration ?? ''} ${selectedDrug?.concentrationUnit ?? ''}, ${selectedDrug?.quantity ?? ''}ml)` : ''}`;
        setSelectedDrug(drug);
        setSearchTermDrugs(formattedDrugValue);
    };

    const handleSelectStorage = (storage) => {
        const formattedStorageValue = `${selectedStorage?.name ?? ''}`;
        setSelectedStorage(storage);
        setSearchTermStorage(formattedStorageValue);
    };

    const handleSelectEmergency = (emergency) => {
        setSelectedEmergency(emergency);
    };

    return (
        <div>
            <KeyboardListener />
            <div className="flex flex-row justify-between items-center mb-4 text-xs sm:text-base">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleNavigateBack}
                    color="primary"
                    className="!text-xs sm:!text-base"
                    size="small"
                    sx={{
                        '@media (min-width: 640px)': {
                            fontSize: 'inherit',
                            padding: '6px 16px',
                        },
                    }}
                >
                    Tillbaka
                </Button>
                <div>
                    <BarCodeOnOffSwitch />
                </div>
            </div>
            {(depositOverview === TransactionOverview.formOverview ||
                depositOverview === TransactionOverview.doubleSignOverview) && (
                <div className="flex min-h-screen bg-gray-50 relative">
                    {/* Stepper Toggle Button - Only visible below 1024px */}
                    <div className="hidden max-lg:block">
                        <button
                            onClick={toggleStepper}
                            className={`fixed top-1/2 -translate-y-1/2 z-50 bg-gray-200 p-2 rounded-r-md shadow-md transition-transform duration-300 ${
                                isStepperOpen
                                    ? 'translate-x-64'
                                    : 'translate-x-0'
                            }`}
                            style={{ left: 0 }}
                        >
                            {isStepperOpen ? (
                                <ChevronLeftIcon />
                            ) : (
                                <ChevronRightIcon />
                            )}
                        </button>
                    </div>

                    {/* Stepper Container */}
                    <div
                        className={`
                            lg:w-1/5 lg:block lg:relative lg:translate-x-0
                            max-lg:fixed max-lg:left-0 max-lg:top-0 max-lg:h-full max-lg:w-64 max-lg:z-40
                            transition-transform duration-300
                            p-5 shadow-xl rounded-lg bg-white
                            ${!isStepperOpen && 'max-lg:-translate-x-full'}
                        `}
                    >
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={index}>
                                    <StepLabel>
                                        <div className="flex flex-col">
                                            <span className="text-lg font-semibold text-gray-900 text-left">
                                                {label}
                                            </span>
                                            <span className="text-sm text-gray-600 italic text-left">
                                                {stepContents[index]}
                                            </span>
                                        </div>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>

                    {/* Overlay - Only visible below 1024px when stepper is open */}
                    {isStepperOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-30 hidden max-lg:block"
                            onClick={toggleStepper}
                        />
                    )}

                    {/* Main Content */}
                    <div className="w-full lg:w-4/5 p-2 sm:p-5 shadow-xl rounded-lg lg:ml-5 mx-2 sm:mx-5 overflow-auto">
                        {/* Navigation Buttons and Title */}
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={4}
                        >
                            <Button
                                disabled={
                                    depositOverview ===
                                        TransactionOverview.doubleSignOverview ||
                                    activeStep === 0
                                }
                                onClick={handleBack}
                                variant="text"
                                startIcon={<ArrowBackIcon />}
                                sx={{
                                    padding: { xs: '4px 8px', sm: '6px 16px' },
                                }}
                            >
                                Tillbaka
                            </Button>
                            <h1 className="text-center text-lg sm:text-2xl">
                                Kassera läkemedel
                            </h1>
                            <Button
                                onClick={handleNext}
                                disabled={activeStep === steps.length - 1}
                                variant="text"
                                endIcon={
                                    activeStep != 3 ? <ArrowForwardIcon /> : ''
                                }
                                sx={{
                                    padding: { xs: '4px 8px', sm: '6px 16px' },
                                }}
                            >
                                {activeStep === steps.length - 1 ? '' : 'Nästa'}
                            </Button>
                        </Box>

                        {/* Step Contents */}
                        {activeStep === 0 && (
                            <div className="flex flex-col items-center">
                                <Box
                                    sx={{
                                        width: { xs: '24rem' },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                    }}
                                >
                                    <Visibility
                                        visible={
                                            formRegistrationMethod === 'manual'
                                        }
                                    >
                                        <AvailableStorages
                                            label="Kassera från"
                                            searchTerm={searchTermStorage}
                                            setSearchTerm={setSearchTermStorage}
                                            handleSelect={handleSelectStorage}
                                            handleSelectEmergency={
                                                handleSelectEmergency
                                            }
                                            storageType={StorageType.storage}
                                            transactionType={
                                                TransactionType.Disposed
                                            }
                                        />
                                    </Visibility>
                                </Box>
                                <Visibility
                                    visible={
                                        formRegistrationMethod === 'barcode'
                                    }
                                >
                                    <ScanningDetector
                                        steps={steps}
                                        activeStep={activeStep}
                                        onScanComplete={() => {
                                            handleNext();
                                        }}
                                    />
                                </Visibility>
                            </div>
                        )}

                        {/* Step 2 */}
                        {activeStep === 1 && (
                            <div className="flex flex-col items-center">
                                <Box
                                    sx={{
                                        width: { xs: '24rem' },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                    }}
                                >
                                    <Visibility
                                        visible={
                                            formRegistrationMethod === 'manual'
                                        }
                                    >
                                        <AvailableDrugs
                                            searchTerm={searchTermDrugs}
                                            setSearchTerm={setSearchTermDrugs}
                                            handleSelect={handleSelectDrug}
                                            storageId={selectedStorage?.id}
                                        />
                                    </Visibility>
                                </Box>
                                <Visibility
                                    visible={
                                        formRegistrationMethod === 'barcode'
                                    }
                                >
                                    <ScanningDetector
                                        steps={steps}
                                        activeStep={activeStep}
                                        onScanComplete={() => {}}
                                    />
                                </Visibility>
                                <Visibility visible={selectedDrug}>
                                    <Box
                                        className="flex mt-4 flex-row space-x-4 justify-center"
                                        sx={{
                                            width: {
                                                sm: '22rem',
                                                xs: '22rem',
                                                lg: '24rem',
                                            },
                                            maxWidth: '100%',
                                            mx: 'auto',
                                        }}
                                    >
                                        <Box sx={{ width: '50%' }}>
                                            <IconTextField
                                                type="number"
                                                size="medium"
                                                id="kasserat"
                                                label="Kassera"
                                                value={disposedQuantity.toString()}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    const clamped = Math.max(
                                                        0,
                                                        Number(v)
                                                    );
                                                    setDisposedQuantity(
                                                        clamped
                                                    );
                                                }}
                                                fullWidth
                                                variant="outlined"
                                                prefix={
                                                    selectedDrug
                                                        ? selectedDrug.quantity +
                                                          ' ' +
                                                          selectedDrug.quantityUnit +
                                                          ' x'
                                                        : ''
                                                }
                                                disabled={
                                                    depositOverview ===
                                                    TransactionOverview.doubleSignOverview
                                                }
                                                aria-label="Enter the amount for Påfyllning"
                                                aria-required="true"
                                            />
                                        </Box>
                                        <Box sx={{ width: '50%' }}>
                                            <TextField
                                                type="number"
                                                id="behallning"
                                                label="Behållning"
                                                value={storageRemaining}
                                                inputProps={{ min: 0 }}
                                                onKeyDown={(e) => {
                                                    if (
                                                        e.key === '-' ||
                                                        e.key.toLowerCase() ===
                                                            'e'
                                                    ) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    const clamped =
                                                        v === ''
                                                            ? ''
                                                            : String(
                                                                  Math.max(
                                                                      0,
                                                                      Number(v)
                                                                  )
                                                              );
                                                    setstorageRemaining(
                                                        clamped
                                                    );
                                                }}
                                                fullWidth
                                                variant="outlined"
                                                disabled={
                                                    depositOverview ===
                                                    TransactionOverview.doubleSignOverview
                                                }
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment
                                                                position="start"
                                                                style={{
                                                                    color: '#aaa',
                                                                }}
                                                            >
                                                                {selectedDrug
                                                                    ? selectedDrug.quantity +
                                                                      ' ' +
                                                                      selectedDrug.quantityUnit +
                                                                      ' x'
                                                                    : ''}
                                                            </InputAdornment>
                                                        ),
                                                        style: {
                                                            textAlign: 'right',
                                                        },
                                                    },
                                                }}
                                                aria-label="Enter the amount for behållning"
                                                aria-required="true"
                                            />
                                        </Box>
                                    </Box>
                                </Visibility>
                            </div>
                        )}
                        {/* Step 3 */}
                        {activeStep === 2 && (
                            <div className="flex flex-col items-center">
                                <Box
                                    sx={{
                                        width: { xs: '24rem' },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                    }}
                                >
                                    <DisposedCause />
                                </Box>
                            </div>
                        )}
                        {/* Step 4 */}
                        {activeStep === 3 && (
                            <div>
                                <Summary
                                    selectedDrug={selectedDrug}
                                    selectedStorage={selectedStorage}
                                    quantityChanged={quantityChanged}
                                    disposedQuantity={disposedQuantity}
                                    storageRemaining={storageRemaining}
                                    selectedEmergency={null}
                                    emergencyRemaining={null}
                                    patientData={null}
                                    note={null}
                                    referensData={null}
                                    transactionType={TransactionType.Disposed}
                                    disposedCause={disposedCause}
                                />
                                <div>
                                    <Sign
                                        transactionType={
                                            TransactionType.Disposed
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            {depositOverview === TransactionOverview.successOverview && (
                <SignedSuccess
                    transactionFormOverview={TransactionType.CheckIn}
                />
            )}
        </div>
    );
};

export default Disposed;
